import { useEffect } from 'react';
import { Button, Message } from '@iwoca/orion';
import { useOffers } from 'components/Signup/hooks/useOffers';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './SubmitDocuments.module.css';

type TProps = {
  isSubmitted: boolean;
  setIsSubmitted: (isSubmitted: boolean) => void;
  updateApplication: () => void;
  isLoading: boolean;
  hasError: boolean;
};

export const SubmitDocuments = ({
  isSubmitted,
  setIsSubmitted,
  updateApplication,
  hasError,
  isLoading,
}: TProps) => {
  const {
    documentRequirements,
    handleDecision,
    isDeciding,
    isRefetchingOffers,
    isReintroduction,
  } = useOffers({
    getOffersEnabled: false,
  });

  const { trackEvent } = useAmplitude();
  const { checkIntroducerPermission } = useIntroducerData();

  useEffect(() => {
    if (!documentRequirements || !isSubmitted) {
      return;
    }

    const requiredDocumentType = documentRequirements.find(
      (requirement) =>
        requirement.is_optional === false &&
        (requirement.status === 'error' || requirement.status === 'required'),
    );

    const elementToScrollTo = document.getElementById(
      `document-requirement-${getDocumentRequirementId(
        requiredDocumentType?.type,
      )}`,
    );

    if (elementToScrollTo) {
      elementToScrollTo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [documentRequirements, isSubmitted]);

  const getDocumentRequirementId = (requirement?: string) => {
    if (!requirement) {
      return null;
    }

    if (requirement === 'company_accounts') {
      // This is because product_offers asks for "company_accounts"
      // But POST documents accepts it under "business accounts"
      return 'business accounts';
    }

    return requirement.split('_').join(' ');
  };

  return (
    <div className={styles.submitContainer}>
      <Button
        loading={isDeciding || isRefetchingOffers || isLoading}
        size="large"
        onClick={async () => {
          trackEvent(AMPLITUDE_EVENTS.submitDocumentsClicked, {
            Reintroduction: isReintroduction,
          });
          await updateApplication();
          await handleDecision();
          setIsSubmitted(true);
        }}
        disabled={!checkIntroducerPermission('can_upload_broker_documents')}
      >
        Submit documents
      </Button>

      {hasError && (
        <Message
          className={styles.errorMessage}
          type="error"
          message="Something went wrong, please try again later."
        />
      )}
    </div>
  );
};
